import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarClock, Loader2, Plug, Unplug } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-01 (Outlook half) — a person's OWN Outlook connection, shown in the BD
 * Assistant settings (reachable by every Stemmler user, unlike the admin-only
 * Integrations page).
 *
 * This is delegated per-user access the BD Assistant uses to read your calendar
 * and draft in your mailbox (drafts only — never sends). It is separate from the
 * tenant mailbox the admin connects; connecting here affects only you.
 *
 * When Microsoft has rejected the stored token (health 'reconnect_required'),
 * the card shows a reconnect banner — the same state the person is DMed about
 * on Slack.
 *
 * Connect is a top-level navigation to Microsoft (the OAuth callback is the
 * shared /api/integrations/outlook/oauth/callback, which routes the per-user
 * state back). On return, ?outlook=connected refreshes the status.
 */

type Health = 'healthy' | 'reconnect_required' | 'not_connected';

interface Connection {
  connected_user_email: string | null;
  connected_user_name: string | null;
  scopes: string[];
  connected_at: string | null;
}

interface Props {
  // Lets a parent (the BD settings page) react to the connection changing, so it
  // can gate Outlook-dependent steps live. Optional — the card works standalone.
  onStatusChange?: (connected: boolean, email: string | null) => void;
}

const BDOutlookPersonalCard: React.FC<Props> = ({ onStatusChange }) => {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [health, setHealth] = useState<Health>('not_connected');
  const [working, setWorking] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Held in a ref so a parent passing a fresh arrow each render does not
  // re-trigger the status load.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/bd-assistant/outlook/status');
      setConnected(Boolean(data?.connected));
      setConnection(data?.connection || null);
      setHealth((data?.health as Health) || (data?.connected ? 'healthy' : 'not_connected'));
      onStatusChangeRef.current?.(Boolean(data?.connected), data?.connection?.connected_user_email || null);
    } catch {
      // Silent: the card simply shows "not connected" if status can't load.
      setConnected(false);
      setConnection(null);
      setHealth('not_connected');
      onStatusChangeRef.current?.(false, null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // OAuth return from Microsoft lands on Settings with ?outlook=…
  useEffect(() => {
    const outcome = searchParams.get('outlook');
    if (!outcome) return;
    if (outcome === 'connected') toast.success('Your Outlook is connected.');
    else if (outcome === 'cancelled') toast('Outlook connection cancelled.');
    else toast.error('Could not connect your Outlook. Try again.');
    searchParams.delete('outlook');
    searchParams.delete('reason');
    searchParams.delete('detail');
    setSearchParams(searchParams, { replace: true });
    void load();
  }, [searchParams, setSearchParams, load]);

  const connect = useCallback(async () => {
    setWorking(true);
    try {
      // Relative /app path only — absolute URLs would be an open-redirect risk
      // when the OAuth callback honors redirect_to. Not the Admin Integrations tab.
      const redirect_to = '/app/settings?tab=bd-assistant&outlook=connected';
      const { data } = await api.post('/api/bd-assistant/outlook/connect', { redirect_to });
      if (data?.authorize_url) {
        window.location.href = data.authorize_url;
      } else {
        toast.error('Could not start the Outlook connection.');
        setWorking(false);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not start the Outlook connection.');
      setWorking(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    setWorking(true);
    try {
      await api.delete('/api/bd-assistant/outlook');
      setConnected(false);
      setConnection(null);
      setHealth('not_connected');
      onStatusChangeRef.current?.(false, null);
      toast.success('Your Outlook was disconnected.');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not disconnect Outlook.');
    } finally {
      setWorking(false);
    }
  }, []);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid="bd-outlook-personal-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <CalendarClock className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Your Outlook (BD Assistant)</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-md">
              Connect your own calendar and mailbox so the BD Assistant can prepare you for
              meetings and draft follow-ups in your voice. Drafts only — nothing is ever sent
              without your approval. This is separate from the firm mailbox an admin connects.
            </p>
          </div>
        </div>
      </div>

      {!loading && health === 'reconnect_required' && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          data-testid="bd-outlook-reconnect-banner"
          role="alert"
        >
          <AlertTriangle className="w-4 h-4 mt-px flex-shrink-0 text-amber-600" />
          <span>
            Your Outlook sign-in has expired, so meeting prep and follow-up drafts are paused.
            Reconnect to pick up where you left off.
          </span>
        </div>
      )}

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking…
          </div>
        ) : connected ? (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Connected{connection?.connected_user_email ? ` as ${connection.connected_user_email}` : ''}.
            </div>
            <button
              type="button"
              onClick={disconnect}
              disabled={working}
              data-testid="bd-outlook-personal-disconnect"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              <Unplug className="w-3.5 h-3.5" /> Disconnect
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={connect}
            disabled={working}
            data-testid="bd-outlook-personal-connect"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-700 disabled:opacity-50"
          >
            {working ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
            {working ? 'Connecting…' : health === 'reconnect_required' ? 'Reconnect my Outlook' : 'Connect my Outlook'}
          </button>
        )}
      </div>
    </div>
  );
};

export default BDOutlookPersonalCard;
